import React from 'react'

import { Stack, Tooltip, Typography } from '@mui/material'

import { Alert, Button, Modal } from '@repo/ui'

interface ConfirmSettingModalProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  /** Question form, naming the change. e.g. "Change when certificates activate?" */
  title: string
  /**
   * What this actually DOES, in the user's terms. The whole reason this modal
   * exists rather than a Save button: a Save button gates the change without ever
   * telling you what it costs. Say the downstream effect explicitly — money moves,
   * live tags repoint, public pages change — not "are you sure?".
   *
   * ReactNode so the one value the reader must actually check (a tag uid, a
   * domain, an amount) can be emphasised rather than buried in flat prose.
   */
  consequence: React.ReactNode
  /** Verb matching the action. Never "OK" / "Confirm". */
  confirmLabel: string
  loading?: boolean
  /**
   * VERA-455 — the confirm is an ACTION, so when the user lacks the permission for it the
   * button is shown DISABLED with a tooltip naming the permission, never hidden
   * (`working-conventions.md` §27/§46). Hiding it would leave a modal that states a
   * consequence and offers no way to act on it, which is strictly worse than a disabled
   * button that says which permission is missing and who to ask.
   *
   * Generic on purpose: the permission lookup belongs at the call site (`useCanAccess` /
   * `PermissionGate`), not in a component whose whole job is "state the consequence, then
   * ask". Passing `confirmDisabled` without `confirmTooltip` is a bug in the caller — a
   * disabled control with nothing to explain it is the silent dead end §46 exists to catch.
   */
  confirmDisabled?: boolean
  confirmTooltip?: React.ReactNode
  /**
   * Plain prose ABOVE the consequence alert — a fact the reader needs in order to
   * weigh the consequence, rather than a second consequence.
   *
   * Exists because callers were reaching for a second `Alert` to carry it, and two
   * stacked alerts read as two warnings of equal weight: the eye has no way to tell
   * which one is the thing being confirmed. Only ONE alert belongs in this modal —
   * the billing/irreversibility consequence — and anything else is context, so it
   * gets the voice of context. Live case: "this certificate has already arrived, so
   * the rule you picked cannot fire" is background to the charge, not a second
   * charge.
   */
  preamble?: React.ReactNode
  /**
   * Render the confirm button as `destructive` (always red, filled) instead of the
   * default `primary`. Off by default — most callers of this modal gate a SETTING
   * change (activation trigger, scan destination), which is high-consequence but not
   * destructive in the §32 sense of a record ceasing to exist. Set this when the
   * action being confirmed actually deletes something (VRG-227's bulk history
   * delete) — the point-of-no-return framing this modal already applies to the
   * copy (§43's warning Alert) should agree with the button's own colour.
   */
  destructive?: boolean
  /**
   * Extra content BELOW the consequence — today, a submit failure from the confirm itself.
   *
   * Deliberately not folded into `consequence`: that slot is inside a warning `Alert`, and an
   * Alert nested in an Alert reads as one message with two severities. A failed confirm is a
   * different fact from the consequence and needs its own tone.
   */
  children?: React.ReactNode
  /**
   * Forwarded to `Modal`'s own `sx`, for the one thing a caller legitimately has
   * to change about the box: its WIDTH.
   *
   * `Modal` is 533px, which is right for a paragraph and an alert. A gate whose
   * `children` is a rendered EMAIL needs more, because the email card is 600px
   * wide and at 533 it reflows to ~500 and reads as a phone screenshot rather
   * than as the message. `maxWidth: calc(100vw - 32px)` still applies underneath,
   * so a wider value degrades on a narrow viewport instead of overflowing (§74).
   *
   * Deliberately the whole `sx` rather than a `width` number: `Modal` already
   * takes `sx` and inventing a second, narrower knob for the same property is how
   * a component ends up with two ways to set one value.
   */
  sx?: object
  /**
   * VRG-739 — a THIRD choice, between Cancel and the confirm, for a gate whose decline is not
   * "do nothing". Saving a narrated article's edit has three outcomes: re-narrate (spends
   * credits: the confirm), save without re-narrating (the alternative), or don't save
   * (Cancel). Folding the alternative into Cancel would make Cancel save, which is the one
   * thing Cancel must never do.
   *
   * Rendered as `secondary`, so the primary button stays the one thing being confirmed. Its
   * label follows the confirm's rule: a verb naming what it does, never "No" / "Skip".
   */
  secondaryAction?: { label: string; onClick: () => void }
}

/**
 * Confirmation gate for a change with downstream or hard-to-reverse effects.
 *
 * Named for settings because that is where it started, and it now also gates
 * discrete ACTIONS with the same property — `ReplaceTagModal` confirms a tag
 * swap through it. The distinction never mattered: what the component encodes
 * is "state the consequence, then ask", which is orthogonal to whether the
 * thing being changed is a setting or an act. Renaming it to
 * `ConfirmActionModal` would be more accurate and touches three call sites;
 * not done, noted.
 *
 * WHEN TO USE THIS vs A SAVE BUTTON — they answer different questions, and a
 * setting can need both, either, or neither:
 *
 *   Save button  ⟸ is the input multi-field or free-text?  (a VALIDITY question —
 *                   partial text is an invalid state, so it needs a commit point)
 *   This modal    ⟸ is the effect downstream or hard to reverse?  (a CONSEQUENCE
 *                   question)
 *
 * So: notification toggles get neither (self-scoped, instantly reversible).
 * Profile fields get a Save only. The certificate activation trigger gets this
 * modal only — one discrete choice, but it changes when certificates become
 * BILLABLE. Renaming a subdomain gets both: it's free text AND it changes the
 * company's public address.
 *
 * IMPORTANT: gate BEFORE applying, never after. If the control visibly changes
 * and then this opens, cancelling produces a flip-and-revert flicker and, for
 * that moment, the UI has lied about what's stored.
 */
export const ConfirmSettingModal = ({
  open,
  onClose,
  onConfirm,
  title,
  consequence,
  confirmLabel,
  loading = false,
  confirmDisabled = false,
  confirmTooltip,
  destructive = false,
  preamble,
  children,
  sx,
  secondaryAction,
}: ConfirmSettingModalProps) => (
  <Modal
    title={title}
    open={open}
    onClose={onClose}
    sx={sx}
    actions={
      <Stack direction="row" spacing={3} alignItems="center">
        <Button variant="secondary" size="medium" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        {secondaryAction && (
          <Button variant="secondary" size="medium" onClick={secondaryAction.onClick} disabled={loading}>
            {secondaryAction.label}
          </Button>
        )}
        {/* A disabled button emits no hover events, so the span is the tooltip's hover target
            — the same wrapper `PermissionGate` uses for the same reason. Rendered only when
            disabled so the enabled path keeps its plain markup. */}
        {confirmDisabled ? (
          <Tooltip title={confirmTooltip ?? ''} arrow>
            <span style={{ display: 'inline-flex', cursor: 'not-allowed' }}>
              <Button variant="primary" size="medium" disabled>
                {confirmLabel}
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            variant="primary"
            size="medium"
            destructive={destructive}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        )}
      </Stack>
    }
  >
    {/* Always a warning, never plain body copy. This started as an opt-in tier
        scoped to "irreversible" settings, on the theory that the scan destination
        was merely high-impact because you can point it back. That was the wrong
        test. The right one is whether harm accrues WHILE the setting is wrong that
        you can't recover: scans taken with location off have no location, forever;
        visitors served without the email gate are never captured; customers who
        scanned a mis-pointed tag already landed in the wrong place. The setting
        flips back, the window doesn't.
        Every gated setting passes that test, so the flag was distinguishing
        nothing. If a genuinely low-severity gate ever appears, reintroduce the
        opt-out then — not before. */}
    <Stack spacing={3}>
      {/* Context first, then the thing being confirmed. Reading order is the point:
          the preamble is what makes the consequence make sense, so it cannot sit
          under it. §15 line-height so a two-sentence note doesn't read as dense. */}
      {preamble && (
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {preamble}
        </Typography>
      )}
      <Alert severity="warning" description={consequence} />
      {children}
    </Stack>
  </Modal>
)
